<template>
  <v-container fluid>
    <v-card class="elevation-8 rounded-lg">
      <!-- Header -->
      <v-card-title class="primary white--text">
        <v-icon left color="white" size="28">mdi-bullhorn</v-icon>
        <span class="text-h5">ประกาศ คัดเลือก/แข่งขัน บุคลากร</span>
        <v-spacer></v-spacer>
        
        <!-- Status Filter -->
        <v-btn-toggle
          v-model="statusFilter"
          mandatory
          class="mr-2"
        >
          <v-btn small value="all" color="white" text>
            <v-icon left>mdi-all-inclusive</v-icon>
            ทั้งหมด
          </v-btn>
          <v-btn small value="active" color="white" text>
            <v-icon left>mdi-lightbulb-on</v-icon>
            เปิดรับ
          </v-btn>
          <v-btn small value="closed" color="white" text>
            <v-icon left>mdi-lightbulb-off</v-icon>
            ปิดรับ
          </v-btn>
        </v-btn-toggle>

        <!-- Refresh Button -->
        <v-btn
          icon
          color="white"
          @click="refreshData"
          :loading="loading"
        >
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Summary Statistics -->
      <v-card-text class="pb-2">
        <v-row>
          <v-col cols="12" md="3" v-for="(stat, index) in summaryStats" :key="index">
            <v-card
              :color="stat.color"
              class="white--text text-center stat-card"
              elevation="4"
            >
              <v-card-text class="py-3">
                <v-icon size="28" class="mb-1">{{ stat.icon }}</v-icon>
                <div class="text-h5 font-weight-bold">{{ stat.value }}</div>
                <div class="text-subtitle-2">{{ stat.title }}</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Search and Filters -->
      <v-card-subtitle class="pb-0">
        <v-row align="center">
          <v-col cols="12" md="5">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="ค้นหาตำแหน่ง, วิทยาลัย หรือรายละเอียด"
              single-line
              hide-details
              dense
              outlined
              clearable
              class="search-field"
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-select
              v-model="positionFilter"
              :items="positionFilterOptions"
              label="ประเภทตำแหน่ง"
              outlined
              dense
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-select
              v-model="hiringFilter"
              :items="hiringFilterOptions"
              label="รูปแบบการจ้าง"
              outlined
              dense
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-select
              v-model="salaryRange"
              :items="salaryRangeOptions"
              label="ช่วงเงินเดือน"
              outlined
              dense
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="12" md="1" class="text-right">
            <!-- Export Menu -->
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="success"
                  v-bind="attrs"
                  v-on="on"
                  small
                  fab
                >
                  <v-icon>mdi-download</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item @click="exportToExcel">
                  <v-list-item-icon>
                    <v-icon>mdi-file-excel</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Excel</v-list-item-title>
                </v-list-item>
                <v-list-item @click="exportToPDF">
                  <v-list-item-icon>
                    <v-icon>mdi-file-pdf-box</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>PDF</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
        </v-row>
      </v-card-subtitle>

      <!-- Enhanced Data Table -->
      <v-card-text class="pa-0">
        <v-data-table
          :loading="loading"
          :headers="enhancedHeaders"
          :items="filteredAnnouncements"
          :search="search"
          :items-per-page="15"
          :footer-props="{
            'items-per-page-options': [10, 15, 25, 50, -1],
            'items-per-page-text': 'รายการต่อหน้า:'
          }"
          loading-text="กำลังโหลดข้อมูล..."
          no-data-text="ไม่มีข้อมูล"
          class="enhanced-table"
          sort-by="temCollegeEmRegDateEnd"
          sort-desc
        >
          <!-- College Name -->
          <template v-slot:item.college_name="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="32" class="mr-2" color="primary">
                <span class="white--text text-caption">{{ getCollegeInitials(item.college_name) }}</span>
              </v-avatar>
              <div>
                <div class="font-weight-medium">{{ item.college_name }}</div>
              </div>
            </div>
          </template>

          <!-- Position with Type Badge -->
          <template v-slot:item.temCollegeEmRegPosition="{ item }">
            <v-chip              
            dark
              small
              label
            >
              {{ getPositionText(item.temCollegeEmRegPosition) }}
            </v-chip>
          </template>

          <!-- Position Details -->
          <template v-slot:item.temCollegeEmRegPositionDetail="{ item }">
            <div class="position-detail">
              <div class="font-weight-medium">{{ item.temCollegeEmRegPositionDetail }}</div>
            </div>
          </template>

          <!-- Hiring Type -->
          <template v-slot:item.temCollegeEmRegHiring="{ item }">
            <v-chip
              :color="getHiringColor(item.temCollegeEmRegHiring)"
              text-color="white"
              small
              outlined
            >
              <v-icon left small>{{ getHiringIcon(item.temCollegeEmRegHiring) }}</v-icon>
              {{ getHiringText(item.temCollegeEmRegHiring) }}
            </v-chip>
          </template>

          <!-- Salary with Currency -->
          <template v-slot:item.temCollegeEmRegSalary="{ item }">
            <div class="salary-display">
              <div class="text-h6 font-weight-bold success--text">
                {{ formatCurrency(item.temCollegeEmRegSalary) }}
              </div>
              <div class="text-caption text--secondary">บาท/เดือน</div>
            </div>
          </template>

          <!-- End Date with Countdown -->
          <template v-slot:item.temCollegeEmRegDateEnd="{ item }">
            <div class="date-display">
              <div class="font-weight-medium">
                {{ formatDate(item.temCollegeEmRegDateEnd) }}
              </div>
              <div class="text-caption" :class="getDateStatusClass(item.temCollegeEmRegDateEnd)">
                {{ getDateStatus(item.temCollegeEmRegDateEnd) }}
              </div>
            </div>
          </template>

          <!-- Application Fee -->
          <template v-slot:item.temCollegeEmPay="{ item }">
            <v-chip
              :color="item.temCollegeEmPay == 0 ? 'success' : 'info'"
              text-color="white"
              small
            >
              {{ item.temCollegeEmPay == 0 ? 'ฟรี' : formatCurrency(item.temCollegeEmPay) + ' บาท' }}
            </v-chip>
          </template>

          <!-- Document Link -->
          <template v-slot:item.temCollegeEmRegDetail="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="info"
                  :href="'/HRvecfiles/' + item.temCollegeEmRegDetail"
                  target="_blank"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>mdi-file-document-outline</v-icon>
                </v-btn>
              </template>
              <span>ดูรายละเอียดประกาศ</span>
            </v-tooltip>
          </template>

          <!-- Status with Animation -->
          <template v-slot:item.temCollegeEmStatus="{ item }">
            <div class="text-center">
              <v-icon
                :color="item.temCollegeEmStatus === '1' || item.temCollegeEmStatus === 1 ? 'success' : 'grey'"
                :class="{ 'pulse-animation': item.temCollegeEmStatus === '1' || item.temCollegeEmStatus === 1 }"
                size="24"
              >
                {{ item.temCollegeEmStatus === '1' || item.temCollegeEmStatus === 1 ? 'mdi-check-circle' : 'mdi-pause-circle' }}
              </v-icon>
              <div class="text-caption mt-1">
                {{ item.temCollegeEmStatus === '1' || item.temCollegeEmStatus === 1   ? 'เปิดรับ' : 'ปิดรับ' }}
              </div>
            </div>
          </template>

          <!-- Actions -->
          <template v-slot:item.actions="{ item }">
            <div class="d-flex">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="primary"
                    @click="viewDetails(item)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon small>mdi-eye</v-icon>
                  </v-btn>
                </template>
                <span>ดูรายละเอียด</span>
              </v-tooltip>
              
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="success"
                    @click="shareAnnouncement(item)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon small>mdi-share</v-icon>
                  </v-btn>
                </template>
                <span>แชร์ประกาศ</span>
              </v-tooltip>
            </div>
          </template>

          <!-- No Results -->
          <template v-slot:no-results>
            <v-alert
              :value="true"
              color="info"
              icon="mdi-information"
              class="ma-4"
            >
              ไม่พบผลลัพธ์สำหรับ "{{ search }}"
            </v-alert>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Job Details Dialog -->
    <v-dialog
      v-model="detailsDialog"
      max-width="800px"
      scrollable
    >
      <v-card v-if="selectedJob">
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-briefcase</v-icon>
          รายละเอียดตำแหน่งงาน
          <v-spacer></v-spacer>
          <v-btn
            icon
            color="white"
            @click="detailsDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text class="pa-6">
          <v-row>
            <v-col cols="12" md="6">
              <v-card outlined class="pa-4">
                <div class="text-h6 mb-3 primary--text">
                  <v-icon left color="primary">mdi-school</v-icon>
                  ข้อมูลหน่วยงาน
                </div>
                <div class="mb-2">
                  <strong>วิทยาลัย:</strong> {{ selectedJob.college_name }}
                </div>
                <div class="mb-2">
                  <strong>ตำแหน่ง:</strong> 
                  <v-chip small :color="getPositionColor(selectedJob.temCollegeEmRegPosition)" text-color="white">
                    {{ getPositionText(selectedJob.temCollegeEmRegPosition) }}
                  </v-chip>
                </div>
                <div class="mb-2">
                  <strong>รายละเอียดตำแหน่ง:</strong> {{ selectedJob.temCollegeEmRegPositionDetail }}
                </div>
              </v-card>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-card outlined class="pa-4">
                <div class="text-h6 mb-3 success--text">
                  <v-icon left color="success">mdi-cash</v-icon>
                  ข้อมูลการจ้างงาน
                </div>
                <div class="mb-2">
                  <strong>รูปแบบการจ้าง:</strong> 
                  <v-chip small :color="getHiringColor(selectedJob.temCollegeEmRegHiring)" outlined>
                    {{ getHiringText(selectedJob.temCollegeEmRegHiring) }}
                  </v-chip>
                </div>
                <div class="mb-2">
                  <strong>เงินเดือน:</strong> 
                  <span class="text-h6 success--text font-weight-bold">
                    {{ formatCurrency(selectedJob.temCollegeEmRegSalary) }} บาท/เดือน
                  </span>
                </div>
                <div class="mb-2">
                  <strong>ค่าสมัคร:</strong> 
                  <v-chip small :color="selectedJob.temCollegeEmPay == 0 ? 'success' : 'info'" text-color="white">
                    {{ selectedJob.temCollegeEmPay == 0 ? 'ฟรี' : formatCurrency(selectedJob.temCollegeEmPay) + ' บาท' }}
                  </v-chip>
                </div>
              </v-card>
            </v-col>
            
            <v-col cols="12">
              <v-card outlined class="pa-4">
                <div class="text-h6 mb-3 warning--text">
                  <v-icon left color="warning">mdi-calendar-clock</v-icon>
                  กำหนดเวลา
                </div>
                <div class="mb-2">
                  <strong>ปิดรับสมัคร:</strong> 
                  <span class="text-h6 font-weight-bold" :class="getDateStatusClass(selectedJob.temCollegeEmRegDateEnd)">
                    {{ formatDate(selectedJob.temCollegeEmRegDateEnd) }}
                  </span>
                </div>
                <div class="mb-2">
                  <strong>สถานะ:</strong> 
                  <v-chip :color="selectedJob.temCollegeEmStatus === '1' || selectedJob.temCollegeEmStatus === 1 ? 'success' : 'grey'" text-color="white">
                    <v-icon left small>{{ selectedJob.temCollegeEmStatus === '1' || selectedJob.temCollegeEmStatus === 1 ? 'mdi-check-circle' : 'mdi-pause-circle' }}</v-icon>
                    {{ selectedJob.temCollegeEmStatus === '1' || selectedJob.temCollegeEmStatus === 1 ? 'เปิดรับสมัคร' : 'ปิดรับสมัคร' }}
                  </v-chip>
                </div>
                <div>
                  <span class="text-caption" :class="getDateStatusClass(selectedJob.temCollegeEmRegDateEnd)">
                    {{ getDateStatus(selectedJob.temCollegeEmRegDateEnd) }}
                  </span>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
        
        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            :href="'/HRvecfiles/' + selectedJob.temCollegeEmRegDetail"
            target="_blank"
          >
            <v-icon left>mdi-file-document</v-icon>
            ดูประกาศฉบับเต็ม
          </v-btn>
          <v-btn
            color="success"
            @click="shareAnnouncement(selectedJob)"
          >
            <v-icon left>mdi-share</v-icon>
            แชร์ประกาศ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar Notification -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
      right
    >
      <v-icon left>{{ snackbar.icon }}</v-icon>
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          ปิด
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import moment from "moment";

export default {
  name: "temporarycollegeemreg",
  
  data() {
    return {
      loading: false,
      ApiKey: "HRvec2021",
      
      // Data
      temporarycollegeemregs: [],
      user: {},
      
      // Filters
      search: "",
      statusFilter: "all",
      positionFilter: null,
      hiringFilter: null,
      salaryRange: null,
      
      // Dialog
      detailsDialog: false,
      selectedJob: null,
      
      // Snackbar
      snackbar: {
        show: false,
        color: "success",
        timeout: 3000,
        icon: "mdi-check",
        text: ""
      },

      // Filter Options
      positionFilterOptions: [
        { text: "พนักงานราชการ(สายการสอน)", value: "governmentteach" },
        { text: "พนักงานราชการ(สายสนับสนุน)", value: "governmentsp" },
        { text: "ครูอัตราจ้าง", value: "temporary" },
        { text: "ลูกจ้างชั่วคราว", value: "temporary_job" }
      ],
      
      hiringFilterOptions: [
        { text: "รายเดือน", value: "month" },
        { text: "รายวัน", value: "day" },
        { text: "รายชั่วโมง", value: "hour" }
      ],
      
      salaryRangeOptions: [
        { text: "ต่ำกว่า 15,000", value: "0-15000" },
        { text: "15,000 - 25,000", value: "15000-25000" },
        { text: "25,000 - 35,000", value: "25000-35000" },
        { text: "35,000 - 50,000", value: "35000-50000" },
        { text: "สูงกว่า 50,000", value: "50000+" }
      ],

      // Enhanced Headers
      enhancedHeaders: [
        {
          text: "วิทยาลัย",
          align: "left",
          value: "college_name",
          width: "200",
          sortable: true
        },
        {
          text: "ประเภทตำแหน่ง",
          align: "center",
          value: "temCollegeEmRegPosition",
          width: "150",
          sortable: true
        },
        {
          text: "รายละเอียดตำแหน่ง",
          align: "left",
          value: "temCollegeEmRegPositionDetail",
          width: "250",
          sortable: true
        },
        {
          text: "การจ้าง",
          align: "center",
          value: "temCollegeEmRegHiring",
          width: "120",
          sortable: true
        },
        {
          text: "เงินเดือน",
          align: "center",
          value: "temCollegeEmRegSalary",
          width: "130",
          sortable: true
        },
        {
          text: "ปิดรับสมัคร",
          align: "center",
          value: "temCollegeEmRegDateEnd",
          width: "150",
          sortable: true
        },
        {
          text: "ค่าสมัคร",
          align: "center",
          value: "temCollegeEmPay",
          width: "100",
          sortable: true
        },
        {
          text: "เอกสาร",
          align: "center",
          value: "temCollegeEmRegDetail",
          width: "80",
          sortable: false
        },
        {
          text: "สถานะ",
          align: "center",
          value: "temCollegeEmStatus",
          width: "100",
          sortable: true
        },
        {
          text: "การดำเนินการ",
          align: "center",
          value: "actions",
          width: "120",
          sortable: false
        }
      ]
    };
  },

  async mounted() {
    await this.initializeData();
  },

  methods: {
    async initializeData() {
      try {
        const userSession = JSON.parse(sessionStorage.getItem("user")) || {};
        if (userSession.user_ID) {
          const result = await this.$http.post("user.php", {
            ApiKey: this.ApiKey,
            user_ID: userSession.user_ID
          });
          this.user = result.data;
        }
        await this.temporarycollegeemregQueryAll();
      } catch (error) {
        this.showSnackbar("เกิดข้อผิดพลาดในการโหลดข้อมูล", "error");
      }
    },

    async temporarycollegeemregQueryAll() {
      this.loading = true;
      try {
        const result = await this.$http.post("temporarycollegeemreg.php", {
          ApiKey: this.ApiKey,
          temCollegeEmRegCode: this.user.college_code
        });
        this.temporarycollegeemregs = result.data || [];
        this.showSnackbar("โหลดข้อมูลเรียบร้อยแล้ว", "success");
      } catch (error) {
        this.showSnackbar("เกิดข้อผิดพลาดในการโหลดข้อมูล", "error");
      } finally {
        this.loading = false;
      }
    },

    async refreshData() {
      await this.temporarycollegeemregQueryAll();
    },

    // Helper Methods
    getPositionText(position) {
      const positions = {
        'governmentteach': 'พนักงานราชการ(สายการสอน)',
        'governmentsp': 'พนักงานราชการ(สายสนับสนุน)',
        'temporary': 'ครูอัตราจ้าง',
        'temporary_job': 'ลูกจ้างชั่วคราว'
      };
      return positions[position] || position;
    },

    getPositionColor(position) {
      const colors = {
        'governmentteach': 'primary',
        'governmentsp': 'info',
        'temporary': 'success',
        'temporary_job': 'warning'
      };
      return colors[position] || 'grey';
    },

    getHiringText(hiring) {
      const hirings = {
        'month': 'รายเดือน',
        'day': 'รายวัน',
        'hour': 'รายชั่วโมง'
      };
      return hirings[hiring] || hiring;
    },

    getHiringColor(hiring) {
      const colors = {
        'month': 'success',
        'day': 'warning',
        'hour': 'info'
      };
      return colors[hiring] || 'grey';
    },

    getHiringIcon(hiring) {
      const icons = {
        'month': 'mdi-calendar-month',
        'day': 'mdi-calendar-today',
        'hour': 'mdi-clock-outline'
      };
      return icons[hiring] || 'mdi-help-circle';
    },

    formatDate(dateString) {
      if (!dateString) return '';
      return moment(dateString).add(543, 'years').format('D MMMM YYYY');
    },

    formatCurrency(amount) {
      if (!amount) return '0';
      return parseInt(amount).toLocaleString();
    },

    getDateStatus(dateString) {
      if (!dateString) return '';
      const endDate = moment(dateString);
      const now = moment();
      const diffDays = endDate.diff(now, 'days');
      
      if (diffDays < 0) return 'หมดเขตแล้ว';
      if (diffDays === 0) return 'หมดเขตวันนี้';
      if (diffDays === 1) return 'หมดเขตพรุ่งนี้';
      if (diffDays <= 7) return `เหลือ ${diffDays} วัน`;
      return `เหลือ ${diffDays} วัน`;
    },

    getDateStatusClass(dateString) {
      if (!dateString) return '';
      const endDate = moment(dateString);
      const now = moment();
      const diffDays = endDate.diff(now, 'days');
      
      if (diffDays < 0) return 'error--text';
      if (diffDays <= 3) return 'warning--text';
      if (diffDays <= 7) return 'orange--text';
      return 'success--text';
    },

    getCollegeInitials(collegeName) {
      if (!collegeName) return '';
      return collegeName.split(' ').map(word => word.charAt(0)).join('').toUpperCase().substring(0, 2);
    },

    viewDetails(item) {
      this.selectedJob = item;
      this.detailsDialog = true;
    },

    shareAnnouncement(item) {
      if (navigator.share) {
        navigator.share({
          title: `ประกาศรับสมัคร ${this.getPositionText(item.temCollegeEmRegPosition)}`,
          text: `${item.college_name} - ${item.temCollegeEmRegPositionDetail}`,
          url: window.location.href
        });
      } else {
        // Fallback for browsers that don't support Web Share API
        navigator.clipboard.writeText(window.location.href);
        this.showSnackbar("คัดลอกลิงก์แล้ว", "info");
      }
    },

    exportToExcel() {
      this.showSnackbar("กำลังส่งออกไฟล์ Excel...", "info");
      // Implementation for Excel export
    },

    exportToPDF() {
      this.showSnackbar("กำลังส่งออกไฟล์ PDF...", "info");
      // Implementation for PDF export
    },

    showSnackbar(text, color = "success") {
      const iconMap = {
        success: "mdi-check-circle",
        error: "mdi-alert-circle",
        warning: "mdi-alert",
        info: "mdi-information"
      };

      this.snackbar = {
        show: true,
        text,
        color,
        icon: iconMap[color] || "mdi-information",
        timeout: 3000
      };
    }
  },

  computed: {
    filteredAnnouncements() {
      let data = [...this.temporarycollegeemregs];
      
      // Status filter
      if (this.statusFilter === "active") {
        data = data.filter(item => item.temCollegeEmStatus === "1");
      } else if (this.statusFilter === "closed") {
        data = data.filter(item => item.temCollegeEmStatus === "0");
      }
      
      // Position filter
      if (this.positionFilter) {
        data = data.filter(item => item.temCollegeEmRegPosition === this.positionFilter);
      }
      
      // Hiring filter
      if (this.hiringFilter) {
        data = data.filter(item => item.temCollegeEmRegHiring === this.hiringFilter);
      }
      
      // Salary range filter
      if (this.salaryRange) {
        data = data.filter(item => {
          const salary = parseInt(item.temCollegeEmRegSalary) || 0;
          switch (this.salaryRange) {
            case "0-15000":
              return salary < 15000;
            case "15000-25000":
              return salary >= 15000 && salary <= 25000;
            case "25000-35000":
              return salary >= 25000 && salary <= 35000;
            case "35000-50000":
              return salary >= 35000 && salary <= 50000;
            case "50000+":
              return salary > 50000;
            default:
              return true;
          }
        });
      }
      
      return data;
    },

    summaryStats() {
      const data = this.temporarycollegeemregs;
      const totalJobs = data.length;
      const activeJobs = data.filter(item => item.temCollegeEmStatus === "1").length;
      const closedJobs = data.filter(item => item.temCollegeEmStatus === "0").length;
      const avgSalary = data.length > 0 
        ? data.reduce((sum, item) => sum + (parseInt(item.temCollegeEmRegSalary) || 0), 0) / data.length 
        : 0;

      return [
        {
          title: "ประกาศทั้งหมด",
          value: totalJobs.toLocaleString(),
          icon: "mdi-briefcase-outline",
          color: "primary"
        },
        {
          title: "เปิดรับสมัคร",
          value: activeJobs.toLocaleString(),
          icon: "mdi-check-circle",
          color: "success"
        },
        {
          title: "ปิดรับสมัคร",
          value: closedJobs.toLocaleString(),
          icon: "mdi-pause-circle",
          color: "warning"
        },
        {
          title: "เงินเดือนเฉลี่ย",
          value: this.formatCurrency(avgSalary.toFixed(0)),
          icon: "mdi-cash",
          color: "info"
        }
      ];
    }
  }
};
</script>

<style scoped>
.job-announcements-container {
  padding: 16px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
}

.stat-card {
  border-radius: 15px !important;
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
}

.search-field {
  border-radius: 25px;
}

.enhanced-table {
  border-radius: 0 0 15px 15px;
  background: white;
}

.enhanced-table >>> .v-data-table__wrapper {
  border-radius: 0 0 15px 15px;
}

.enhanced-table >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.enhanced-table >>> .v-data-table-header th {
  background: linear-gradient(145deg, #1976d2 0%, #1565c0 100%) !important;
  color: white !important;
  font-weight: 600;
  border: none !important;
}

.position-detail {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.salary-display {
  text-align: center;
}

.date-display {
  text-align: center;
}

.v-chip {
  transition: all 0.3s ease;
}

.v-chip:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.v-btn-toggle {
  border-radius: 25px !important;
  overflow: hidden;
}

.v-btn-toggle >>> .v-btn {
  border-radius: 0 !important;
}

/* Pulse animation for active status */
.pulse-animation {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

/* Export menu styling */
.v-menu >>> .v-list {
  border-radius: 10px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.v-menu >>> .v-list-item:hover {
  background: linear-gradient(145deg, #f5f5f5 0%, #e8e8e8 100%);
}

/* Custom scrollbar for table */
.enhanced-table >>> .v-data-table__wrapper::-webkit-scrollbar {
  height: 8px;
  width: 8px;
}

.enhanced-table >>> .v-data-table__wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.enhanced-table >>> .v-data-table__wrapper::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.enhanced-table >>> .v-data-table__wrapper::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Loading states */
.v-data-table >>> .v-data-table__progress {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Snackbar styling */
.v-snackbar >>> .v-snack__wrapper {
  border-radius: 10px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

/* Button hover effects */
.v-btn {
  transition: all 0.3s ease;
}

.v-btn:hover:not(.v-btn--disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

/* Card title styling */
.v-card-title {
  background: linear-gradient(145deg, #1976d2 0%, #1565c0 100%);
  border-radius: 15px 15px 0 0;
}

/* Form field styling */
.v-text-field--outlined >>> fieldset,
.v-select--outlined >>> fieldset {
  border-color: #e0e0e0;
  border-radius: 15px;
}

.v-text-field--outlined:hover >>> fieldset,
.v-select--outlined:hover >>> fieldset {
  border-color: #1976d2;
}

.v-text-field--outlined.v-input--is-focused >>> fieldset,
.v-select--outlined.v-input--is-focused >>> fieldset {
  border-color: #1976d2;
  border-width: 2px;
}

/* Dialog styling */
.v-dialog >>> .v-card {
  border-radius: 15px !important;
}

/* Avatar styling */
.v-avatar {
  font-weight: bold;
}

/* Responsive design */
@media (max-width: 1024px) {
  .enhanced-table >>> .v-data-table__wrapper {
    max-height: 500px;
  }
}

@media (max-width: 768px) {
  .job-announcements-container {
    padding: 8px;
  }
  
  .stat-card {
    margin-bottom: 8px;
  }
  
  .enhanced-table >>> .v-data-table__wrapper {
    max-height: 400px;
  }
  
  .v-card-title {
    font-size: 1.1rem !important;
    flex-direction: column;
    align-items: flex-start;
  }
  
  .v-card-title .text-h5 {
    font-size: 1.1rem !important;
    margin-bottom: 8px;
  }
  
  .v-btn-toggle {
    margin-bottom: 8px;
    width: 100%;
  }
  
  /* Hide some columns on mobile */
  .enhanced-table >>> th:nth-child(3),
  .enhanced-table >>> th:nth-child(7),
  .enhanced-table >>> td:nth-child(3),
  .enhanced-table >>> td:nth-child(7) {
    display: none;
  }
  
  /* Adjust position detail column width */
  .position-detail {
    max-width: 150px;
  }
}

@media (max-width: 480px) {
  .v-btn-toggle {
    flex-direction: column;
  }
  
  .v-btn-toggle >>> .v-btn {
    width: 100%;
    margin-bottom: 4px;
    border-radius: 8px !important;
  }
  
  .stat-card >>> .v-card-text {
    padding: 12px;
  }
  
  .stat-card >>> .text-h5 {
    font-size: 1.3rem !important;
  }
  
  /* Stack filter controls vertically on small screens */
  .v-card-subtitle >>> .v-row {
    flex-direction: column;
  }
  
  .v-card-subtitle >>> .v-col {
    margin-bottom: 8px;
  }
}

/* Print styles */
@media print {
  .job-announcements-container {
    background: white !important;
    padding: 0;
  }
  
  .v-btn,
  .search-field,
  .v-btn-toggle,
  .v-menu {
    display: none !important;
  }
  
  .v-card {
    box-shadow: none !important;
    border: 1px solid #ddd;
  }
  
  .enhanced-table >>> .v-data-table__wrapper {
    max-height: none !important;
  }
}

/* Dark theme support */
.theme--dark .enhanced-table {
  background: #2d2d2d;
}

.theme--dark .stat-card {
  background: linear-gradient(145deg, #2d2d2d 0%, #1e1e1e 100%);
}

/* Accessibility improvements */
.v-btn:focus,
.v-text-field:focus,
.v-select:focus {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .v-chip {
    border: 2px solid currentColor;
  }
  
  .enhanced-table >>> tbody tr:hover {
    background-color: #000 !important;
    color: #fff !important;
  }
}

/* Reduced motion for accessibility */
@media (prefers-reduced-motion: reduce) {
  .stat-card:hover,
  .v-btn:hover,
  .v-chip:hover {
    transform: none;
  }
  
  .pulse-animation {
    animation: none;
  }
  
  .v-data-table >>> .v-data-table__progress {
    animation: none;
  }
}

/* Focus indicators for better accessibility */
.v-chip:focus {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}

/* Status colors for better visibility */
.success--text {
  color: #4caf50 !important;
}

.warning--text {
  color: #ff9800 !important;
}

.error--text {
  color: #f44336 !important;
}

.orange--text {
  color: #ff5722 !important;
}
.success {
  color: #4caf50 !important;
}
.warning {
  color: #ff9800 !important;
}
.error {
  color: #f44336 !important;
} 
</style>